// star-gate.ts — why the ★ is not on the card.
//
// Born 2026-09-01, after two people independently reported the favourites star
// missing from the widget. Nothing on the wire could settle it. The control is
// gated at renderer.ts:3201 by `canStar()` → `favourites.canCallAccount()` →
// `DigitainFeedSource.canCallAccount()`, which needs a shadowed contentWindow AND
// either a captured authenticated template or (an anonymous template + a JWT read
// out of the operator's app). Four inputs, none of them reported anywhere: the
// `?bcdiag=1` strip printed the favourites CACHE, which is a different question,
// and `star_match` only ever fires when the gate is already open — an absence
// there is consistent with every cause at once.
//
// ⛔ ONE reading is not enough, and that is the whole design here. All four inputs
// are captured in flight from operator traffic, so a gate that is shut when the
// first card paints may well open a moment later. "Shut forever" and "shut for the
// first ten seconds" call for completely different fixes — a broken capture versus
// a card that never re-renders — and a single sample cannot tell them apart. So
// the probe takes the reading at the first impression, waits, takes a second, and
// sends BOTH in one event. One event per session, and it answers the question.

/** The four gate inputs plus the verdict they produce. */
export interface StarGateRead {
  /** A shadowed `contentWindow` to replay through. Without it nothing is reachable. */
  cw: boolean;
  /** The anonymous odds template — replayable, but it carries no identity. */
  tpl: boolean;
  /** A captured AUTHENTICATED template. On its own this is enough. */
  auth: boolean;
  /** A JWT readable from the operator's app, which upgrades `tpl` to authenticated. */
  jwt: boolean;
  /** What `canCallAccount()` actually returned — recorded, never recomputed here,
   *  so a future change to the gate cannot silently desync from its own probe. */
  can: boolean;
}

type Client = { sendEvent: (a: string, b: string, c: string, d: null, meta: unknown) => void };
type Schedule = (fn: () => void, ms: number) => unknown;
/** Has this session already sent (or armed) the probe, and mark that it has.
 *  ⛔ An instance flag is NOT a session: the host is an SPA that REMOUNTS the SDK
 *  on every navigation, so a closure variable arms a fresh probe per mount and
 *  `star_gate` arrives once per PAGE VIEW on an API that has already hit its rate
 *  limit. The caller passes a sessionStorage-backed pair (see index.ts), exactly
 *  like the slip nudge's `bc_slip_nudge` flag. */
export interface SessionOnce { done: () => boolean; mark: () => void }

/** ms between the two readings. Long enough for a slow board to finish its first
 *  authenticated request, short enough that the session is usually still alive. */
export const STAR_GATE_DELAY_MS = 30000;

/**
 * Returns a function to call on every impression. The FIRST call takes reading
 * one and arms the timer; every later call is a no-op. Nothing reaches the wire
 * until the second reading is in, so the event is never sent without its pair.
 */
export function makeStarGateProbe(
  client: Client,
  read: () => StarGateRead,
  delayMs: number = STAR_GATE_DELAY_MS,
  schedule: Schedule = setTimeout,
  once: SessionOnce = { done: () => false, mark: () => { /* per-instance only */ } },
): () => void {
  let armed = false;
  return () => {
    if (armed) return;
    armed = true;
    // Marked at ARM time, not at send time: the 30s wait very often outlives the
    // mount, and a probe that only marked on send would re-arm on every remount
    // and still send nothing. One reading pair per session is the whole design.
    if (once.done()) return;
    once.mark();
    // A diagnostic that can break the widget is worse than the missing star.
    try {
      const at_first = read();
      schedule(() => {
        try {
          client.sendEvent("", "star_gate", "", null, { at_first, later: read() });
        } catch { /* the probe is never worth an exception */ }
      }, delayMs);
    } catch { /* same */ }
  };
}

/** One line for the `?bcdiag=1` strip. Names the MISSING inputs rather than
 *  printing four yes/no pairs — on a phone the question is only ever "which one". */
export function starGateLine(r: StarGateRead): string {
  if (r.can) return "★ yes";
  const missing = (["cw", "tpl", "auth", "jwt"] as const).filter((k) => !r[k]);
  return missing.length ? `★ NO (${missing.join(" ")})` : "★ NO";
}
